import React, { useEffect, useState } from 'react';
import {
  Box,
  Title,
  Text,
  useMantineColorScheme,
  Stack,
  Group,
  Button,
  TextInput,
  Divider,
  LoadingOverlay,
  Checkbox,
  Anchor,
  Image,
} from '@mantine/core';
import PageTransition from '../components/animations/PageTransition';
import { subscriptionPlanService, SubscriptionPlanDTO } from '../services/subscriptionPlanService';
import { userSubscriptionService } from '../services/userSubscriptionService';
import { discountCouponService, type CouponValidationResultDTO } from '../services/discountCouponService';
import { mercadoPagoService } from '../services/mercadoPagoService';
import MercadoPagoCardForm from '../components/payments/MercadoPagoCardForm';
import { authService } from '../services/authService';
import { notifications } from '@mantine/notifications';
import { useNavigate, useParams } from 'react-router-dom';
import { IconCheck, IconX, IconCreditCard } from '@tabler/icons-react';
import enarmLogo from '../assets/enarm_logo_noletter.png';

const CheckoutPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<SubscriptionPlanDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [mpReady, setMpReady] = useState(false);

  // Form state
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<CouponValidationResultDTO | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const [processing, setProcessing] = useState(false);

  const currentUser = authService.getCurrentUserFromStorage();
  const userRole = authService.isAdmin() ? 'admin' : authService.isEstudiante() ? 'student' : null;

  useEffect(() => {
    const loadPlan = async () => {
      if (!planId) {
        navigate('/mejorarplan');
        return;
      }
      try {
        setLoading(true);
        const plans = await subscriptionPlanService.getAllActivePlans();
        const selectedPlan = plans.find((p) => p.id === Number(planId));
        if (!selectedPlan) {
          notifications.show({ color: 'red', message: 'Plan no encontrado' });
          navigate('/mejorarplan');
          return;
        }
        setPlan(selectedPlan);
      } catch (e: any) {
        console.error(e);
        notifications.show({ color: 'red', message: 'Error al cargar el plan' });
        navigate('/mejorarplan');
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, [planId, navigate]);

  // Validar configuración de Mercado Pago para inicializar el Brick
  useEffect(() => {
    (async () => {
      try {
        const ok = await mercadoPagoService.validateConfiguration();
        if (!ok) notifications.show({ color: 'red', message: 'Mercado Pago no está configurado' });
        setMpReady(ok);
      } catch {
        setMpReady(false);
      }
    })();
  }, []);

  const applyCoupon = async () => {
    if (!couponCode.trim() || !plan) return;
    const amount = Number(plan.price);
    if (!Number.isFinite(amount)) {
      notifications.show({ color: 'red', message: 'Precio del plan inválido' });
      return;
    }
    try {
      setApplyingCoupon(true);
      const result = await discountCouponService.validate({
        couponCode: couponCode.trim(),
        planId: plan.id,
        amount,
      });
      setCouponResult(result);
      if (result.isValid) {
        notifications.show({ color: 'green', message: result.message || 'Cupón aplicado' });
      } else {
        notifications.show({ color: 'orange', message: result.message || 'Cupón inválido' });
      }
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al validar el cupón' });
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponResult(null);
  };

  const handleSubmit = async () => {
    if (!plan) return;

    // Si el plan es gratuito, procesar directamente
    if (plan.price === 0) {
      return handleFreeSubscription();
    }

    // Para planes de pago, usar MercadoPago
    return handlePaidSubscription();
  };

  const handleFreeSubscription = async () => {
    try {
      setProcessing(true);
      const payload = {
        planId: plan!.id,
        couponCode: couponResult?.isValid ? couponCode.trim() : undefined,
        paymentMethod: 'FREE',
      };
      await userSubscriptionService.createSubscription(payload);
      notifications.show({ 
        color: 'green', 
        title: 'Suscripción activada',
        message: 'Tu plan gratuito ha sido activado exitosamente' 
      });
      navigate('/settings/billing');
    } catch (e: any) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: e?.response?.data?.error || 'No se pudo activar la suscripción',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePaidSubscription = async () => {
    // El pago se maneja directamente a través del MercadoPagoCardForm
    notifications.show({ 
      color: 'blue', 
      message: 'Completa los datos de la tarjeta para procesar el pago con Mercado Pago' 
    });
  };

  const finalPrice = couponResult?.isValid ? Number(couponResult.finalAmount) : Number(plan?.price ?? 0);

  return (
    <PageTransition type="medical" duration={600}>
      <Box style={{ 
        minHeight: '100vh', 
        display: 'grid', 
        gridTemplateColumns: 'minmax(280px, 38%) 1fr',
        background: colorScheme === 'dark' ? '#1a202c' : '#faf8f3'
      }}>
        {/* Columna izquierda: marca y resumen */}
        <Box
          style={{
            color: colorScheme === 'dark' ? 'white' : '#1e293b',
            padding: '48px 32px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            borderRight: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}
        >
          <Stack gap="xl">
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <div>
                <Image src={enarmLogo} alt="ENARM360" height={48} fit="contain" />
              </div>
              <Anchor 
                onClick={() => {
                  if (userRole === 'admin') {
                    navigate('/admin/dashboard');
                  } else if (userRole === 'student') {
                    navigate('/estudiante/dashboard');
                  } else {
                    navigate('/');
                  }
                }} 
                style={{ color: colorScheme === 'dark' ? 'white' : '#1e293b', opacity: 0.8, cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}
              >
                ← Volver al Dashboard
              </Anchor>
            </Group>

            {/* Resumen del plan */}
            {plan && (
              <Stack gap={8} mt="xl">
                <Text fw={700} size="xl">{plan.name}</Text>
                <Text size="sm" opacity={0.7}>{plan.description || 'Plan de suscripción'}</Text>
                <Group gap={12} mt="sm">
                  {couponResult?.isValid && (
                    <Text size="sm" opacity={0.5} style={{ textDecoration: 'line-through' }}>
                      ${Number(plan.price)}
                    </Text>
                  )}
                  <Text fw={800} size="2.5rem" style={{ lineHeight: 1 }}>${finalPrice}</Text>
                  <Text size="sm" opacity={0.7}>/{plan.billingInterval === 'YEARLY' ? 'año' : 'mes'}</Text>
                </Group>
              </Stack>
            )}

            {/* Cupón */}
            <Stack gap="xs" mt="lg">
              <Text size="sm" fw={600}>Código de descuento</Text>
              <Group align="flex-end">
                <TextInput
                  placeholder="Ingresa tu código"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.currentTarget.value)}
                  disabled={!!couponResult?.isValid}
                  style={{ flex: 1 }}
                />
                {couponResult?.isValid ? (
                  <Button variant="light" color="gray" onClick={removeCoupon}>Quitar</Button>
                ) : (
                  <Button variant="light" onClick={applyCoupon} loading={applyingCoupon}>Aplicar</Button>
                )}
              </Group>
              {couponResult && (
                <Group gap="xs">
                  {couponResult.isValid ? <IconCheck size={16} color="#10b981" /> : <IconX size={16} color="#ef4444" />}
                  <Text size="sm" c={couponResult.isValid ? 'teal' : 'red'}>
                    {couponResult.message || (couponResult.isValid ? 'Cupón aplicado' : 'Cupón inválido')}
                  </Text>
                </Group>
              )}
            </Stack>
          </Stack>

          <Stack gap={12} align="flex-start">
            <div style={{ marginLeft: '-6px' }}>
              <Image src="/mercado_pago_logo.png" alt="Mercado Pago" height={72} fit="contain" style={{ maxWidth: '240px', display: 'block' }} />
            </div>
            <Group gap={4} style={{ fontSize: '12px', opacity: 0.7 }}>
              <Anchor 
                onClick={() => navigate('/about')} 
                style={{ color: colorScheme === 'dark' ? 'white' : '#1e293b', fontSize: '12px' }}
              >
                Conoce más sobre nosotros
              </Anchor>
              <Text span>|</Text>
              <Anchor 
                onClick={() => navigate('/terms')} 
                style={{ color: colorScheme === 'dark' ? 'white' : '#1e293b', fontSize: '12px' }}
              >
                Términos
              </Anchor>
              <Text span>|</Text>
              <Anchor 
                onClick={() => navigate('/privacy')} 
                style={{ color: colorScheme === 'dark' ? 'white' : '#1e293b', fontSize: '12px' }}
              >
                Privacidad
              </Anchor>
            </Group>
          </Stack>
        </Box>

        {/* Columna derecha: formulario */}
        <Box p="xl" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
          <LoadingOverlay visible={loading || !mpReady} />
          {plan && (
            <Stack gap="xl" style={{ width: '100%', maxWidth: '680px' }}>
              {/* Pago (Mercado Pago Brick) */}
              <Stack gap="sm">
                <div>
                  {plan && currentUser && mpReady && (
                    <MercadoPagoCardForm
                      planId={plan.id}
                      amount={finalPrice}
                      user={currentUser}
                      couponCode={couponResult?.isValid ? couponCode.trim() : undefined}
                      savePaymentMethod={savePaymentMethod}
                      onSuccess={() => navigate('/payment-success')}
                      onError={(msg) => notifications.show({ color: 'red', message: msg })}
                    />
                  )}
                </div>
                <Checkbox
                  label="Guardar tarjeta para renovaciones"
                  checked={savePaymentMethod}
                  onChange={(event) => setSavePaymentMethod(event.currentTarget.checked)}
                />
              </Stack>

              {plan?.price === 0 && (
                <Button size="md" onClick={handleSubmit} loading={processing}>
                  Activar plan gratuito
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </PageTransition>
  );
};

export default CheckoutPage;
